import { qz } from "../client";
import { createNamespaceStore } from "../internal/createNamespaceStore";
import { type SerialEvent, type SerialState, type SerialStatus } from "./types";

const MAX_SERIAL_EVENTS = 100;

const toSerialStatus = (
  event: SerialEvent,
  updatedAt: number,
): SerialStatus => ({
  lastEventKind: event.kind,
  lastException: event.kind === "ERROR" ? event.exception : null,
  lastOutput: event.kind === "RECEIVE" ? event.output : null,
  portName: event.portName,
  updatedAt,
});

const parseSerialEvent = (raw: unknown): SerialEvent | null => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const portName = typeof data.portName === "string" ? data.portName : null;

  if (!portName) return null;

  if (typeof data.exception === "string")
    return { exception: data.exception, kind: "ERROR", portName, raw };

  if (typeof data.output === "string")
    return { kind: "RECEIVE", output: data.output, portName, raw };

  return null;
};

export const serialStore = createNamespaceStore<SerialState, SerialEvent>({
  applyEvent: (state, event, at) => ({
    ...state,
    events: [...state.events, event].slice(-MAX_SERIAL_EVENTS),
    lastEventAt: at,
    statusByPort: {
      ...state.statusByPort,
      [event.portName]: toSerialStatus(event, at),
    },
  }),
  createInitialState: () => ({
    events: [],
    lastEventAt: null,
    lastListenerError: null,
    openPorts: [],
    statusByPort: {},
  }),
  label: "serial",
  parseEvent: parseSerialEvent,
  registerCallback: (handler) => qz.serial.setSerialCallbacks(handler),
});

export const markPortOpen = (port: string) => {
  serialStore.setState((s) => ({
    ...s,
    openPorts: s.openPorts.includes(port)
      ? s.openPorts
      : [...s.openPorts, port],
  }));
};

export const markPortClosed = (port: string) => {
  serialStore.setState((s) => ({
    ...s,
    openPorts: s.openPorts.filter((openPort) => openPort !== port),
  }));
};
